/**
 * Error Message Component
 * Display error messages from API with optional WhatsApp support CTA
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { openWhatsAppSupport } from '../../utils/whatsapp';
import type { ApiError } from '../../api/types';

interface ErrorMessageProps {
  error: string | ApiError | null;
  onDismiss?: () => void;
  /** WhatsApp support context string (e.g. "Erreur chargement budget"). Set to false to hide. Defaults to auto. */
  whatsappContext?: string | false;
  style?: any;
}

export default function ErrorMessage({
  error,
  onDismiss,
  whatsappContext,
  style,
}: ErrorMessageProps) {
  if (!error) return null;

  // Extract error message
  let errorMessage = '';
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error.errors) {
    // API error format
    const firstError = Object.values(error.errors)[0];
    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
  }

  if (!errorMessage) return null;

  const showWhatsApp = whatsappContext !== false;
  const context = typeof whatsappContext === 'string' ? whatsappContext : errorMessage;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.content}>
        <Text style={styles.text}>{errorMessage}</Text>
        {showWhatsApp && (
          <TouchableOpacity
            onPress={() => openWhatsAppSupport(context)}
            style={styles.whatsappLink}
          >
            <Text style={styles.whatsappLinkText}>Contacter le support</Text>
          </TouchableOpacity>
        )}
      </View>
      {onDismiss && (
        <TouchableOpacity onPress={onDismiss} style={styles.dismissButton}>
          <Text style={styles.dismissText}>✕</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFF3F3',
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
    padding: spacing.md,
    borderRadius: 8,
    marginVertical: spacing.sm,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
  },
  text: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
  },
  whatsappLink: {
    marginTop: spacing.xs,
  },
  whatsappLinkText: {
    fontSize: typography.fontSize.xs,
    color: '#25D366',
    fontWeight: typography.fontWeight.semibold as '600',
  },
  dismissButton: {
    marginLeft: spacing.sm,
    padding: spacing.xs,
  },
  dismissText: {
    fontSize: typography.fontSize.md,
    color: colors.error,
    fontWeight: typography.fontWeight.bold,
  },
});

